import { Space } from "antd";
import type { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useParams } from "react-router";
import { StyledButton } from "~Common/Components";
import PermissionCheck from "~Common/PermissionCheck";
import { ImageSizes } from "~enums/imageSizes.ts";
import { ImageTypes } from "~enums/imageTypes.ts";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import CheckBoxEditableColumn from "~pages/Roster/components/RosterTable/components/CheckBoxEditableColumn";
import DeleteRosterAction from "~pages/Roster/components/RosterTable/components/DeleteRosterAction";
import PositionEditableColumn from "~pages/Roster/components/RosterTable/components/PositionEditableColumn";
import RosterStatusEditableColumn from "~pages/Roster/components/RosterTable/components/RosterStatusEditableColumn";
import StringEditableColumn from "~pages/Roster/components/RosterTable/components/StringEditableColumn";
import type { RosterTableRow } from "~pages/Roster/components/RosterTable/types/rosterTableRow";
import { sortStringValues } from "~utils/utilities.ts";
import ImageView from "../../../../../components/ImageView/ImageView.tsx";

export const useGetRosterColumns = (
  massEditMode: boolean,
  upsertRoster: (value: any) => Promise<void>,
  handleDelete: (id: string) => Promise<void>,
  deleteCaptainRole: (id: string) => Promise<unknown>,
  createCaptainRole: (personId: string) => void,
  isAddToStarterAllowed: boolean,
  isRosterEditable: boolean,
) => {
  const { formatMessage } = useIntl();
  const [isUpsertingMap, setIsUpsertingMap] = useState<Record<string, boolean>>({});

  const { customerId, sport, organizationId, fixtureId } = useParams();

  const handleSave = (row: RosterTableRow, columnId: string) => {
    return async (value: string | boolean) => {
      if (row[columnId] === value) return;
      setIsUpsertingMap((prev) => ({ ...prev, [row.personId]: true }));
      await upsertRoster({
        personId: row.personId,
        position: row.position,
        ...(!fixtureId && { status: row.status }),
        bib: row.bib,
        [columnId]: value,
      });
      setIsUpsertingMap((prev) => ({ ...prev, [row.personId]: false }));
    };
  };

  const columns: ColumnType<RosterTableRow>[] = [
    {
      title: formatMessage({ id: "roster.number" }),
      key: "bib",
      dataIndex: "bib",
      sorter: (a, b) => sortStringValues(a.bib, b.bib),
      width: 50,
      render: (value: string, row: RosterTableRow) => (
        <StringEditableColumn
          disabled={isUpsertingMap[row.personId] || false}
          isEditMode={massEditMode}
          defaultValue={value}
          onSave={handleSave(row, "bib")}
        />
      ),
    },
    {
      title: formatMessage({ id: "name.local.full" }),
      dataIndex: "nameFullLocal",
      key: "nameFullLocal",
      sorter: (a, b) => sortStringValues(a.nameFullLocal, b.nameFullLocal),
      width: 200,
      render: (value: string, row: RosterTableRow) => (
        <Space size="small">
          <ImageView imageUrl={row.avatarUrl} size={ImageSizes.Small} width={25} height={25} imageType={ImageTypes.PersonHead} />
          {value}
        </Space>
      ),
    },
    {
      title: formatMessage({ id: "roster.person.id" }),
      dataIndex: "personId",
      key: "personId",
      width: 210,
    },
    {
      title: formatMessage({ id: "person.externalId" }),
      dataIndex: ["person", "externalId"],
      key: "person.externalId",
      width: 100,
    },
    {
      title: formatMessage({ id: "nameFullLatin" }),
      dataIndex: ["person", "nameFullLatin"],
      key: "person.nameFullLatin",
      width: 200,
    },
    {
      title: formatMessage({ id: "date.of.birth" }),
      dataIndex: "dob",
      key: "dob",
      width: 100,
      render: (value) => <>{value ? dayjs(value).format("DD/MM/YYYY") : ""}</>,
    },
    {
      title: formatMessage({ id: "gender" }),
      dataIndex: ["person", "gender"],
      key: "person.gender",
      width: 90,
    },
    {
      title: formatMessage({ id: "nationality" }),
      dataIndex: ["person", "nationality"],
      key: "person.nationality",
      width: 50,
    },
    {
      title: formatMessage({ id: "representing" }),
      dataIndex: ["person", "representing"],
      key: "person.representing",
      width: 100,
    },
    {
      title: formatMessage({ id: "person.status" }),
      dataIndex: ["person", "status"],
      key: "person.status",
      width: 100,
    },
    {
      title: formatMessage({ id: "person.additionalDetails.height" }),
      dataIndex: ["person", "additionalDetails", "height"],
      key: "person.additionalDetails.height",
      width: 30,
    },
    {
      title: formatMessage({ id: "person.additionalDetails.weight" }),
      dataIndex: ["person", "additionalDetails", "weight"],
      key: "person.additionalDetails.weight",
      width: 30,
    },
    {
      title: formatMessage({ id: "person.additionalDetails.heightImperial" }),
      dataIndex: ["person", "additionalDetails", "heightImperial"],
      key: "person.additionalDetails.heightImperial",
      width: 90,
    },
    {
      title: formatMessage({ id: "person.additionalDetails.weightImperial" }),
      dataIndex: ["person", "additionalDetails", "weightImperial"],
      key: "person.additionalDetails.weightImperial",
      width: 90,
    },
    {
      title: formatMessage({ id: "person.additionalDetails.representation" }),
      dataIndex: ["person", "additionalDetails", "representation"],
      key: "person.additionalDetails.representation",
      width: 90,
    },
    {
      title: formatMessage({
        id: "person.additionalDetails.juniorAssociationLeague",
      }),
      dataIndex: ["person", "additionalDetails", "juniorAssociationLeague"],
      key: "person.additionalDetails.juniorAssociationLeague",
      width: 90,
    },
    {
      title: formatMessage({ id: "person.additionalDetails.homeTown" }),
      dataIndex: ["person", "additionalDetails", "homeTown"],
      key: "person.additionalDetails.homeTown",
      width: 90,
    },
    {
      title: formatMessage({ id: "person.additionalDetails.college" }),
      dataIndex: ["person", "additionalDetails", "college"],
      key: "person.additionalDetails.college",
      width: 90,
    },
    {
      title: formatMessage({ id: "person.additionalDetails.collegeClass" }),
      dataIndex: ["person", "additionalDetails", "collegeClass"],
      key: "person.additionalDetails.collegeClass",
      width: 90,
    },
    {
      title: formatMessage({ id: "person.additionalDetails.school" }),
      dataIndex: ["person", "additionalDetails", "school"],
      key: "person.additionalDetails.school",
      width: 90,
    },
    {
      title: formatMessage({ id: "person.additionalDetails.schoolClass" }),
      dataIndex: ["person", "additionalDetails", "schoolClass"],
      key: "person.additionalDetails.schoolClass",
      width: 90,
    },
    {
      title: formatMessage({ id: "externalId" }),
      dataIndex: "externalId",
      key: "externalId",
      width: 100,
    },
    {
      title: formatMessage({ id: "position" }),
      sorter: (a, b) => sortStringValues(a.position, b.position),
      dataIndex: "position",
      key: "position",
      width: 100,

      fixed: "right",
      render: (value: string, row: RosterTableRow) => (
        <PositionEditableColumn
          disabled={isUpsertingMap[row.personId] || false}
          isEditMode={massEditMode}
          defaultValue={value}
          onSave={handleSave(row, "position")}
        />
      ),
    },
    {
      title: formatMessage({ id: "roster.starter" }),
      dataIndex: "starter",
      key: "starter",
      fixed: "right",
      width: 20,
      render: (value: boolean, row: RosterTableRow) => {
        return (
          <CheckBoxEditableColumn
            disabled={!isRosterEditable || isUpsertingMap[row.personId]}
            isAddDisabled={!isAddToStarterAllowed}
            defaultValue={value}
            onSave={handleSave(row, "starter")}
          />
        );
      },
    },
    {
      title: formatMessage({ id: "roster.captain" }),
      dataIndex: "isCaptain",
      key: "isCaptain",
      fixed: "right",
      width: 20,
      render: (value: boolean, row: RosterTableRow) => {
        return (
          <CheckBoxEditableColumn
            defaultValue={value}
            disabled={!isRosterEditable || isUpsertingMap[row.personId]}
            onSave={(val) => {
              if (!val && row.captainRoleId) {
                deleteCaptainRole(row.captainRoleId);
              } else if (val && !row.captainRoleId) {
                createCaptainRole(row.personId);
              }
            }}
          />
        );
      },
    },
    {
      title: formatMessage({ id: "roster.status" }),
      sorter: (a, b) => sortStringValues(a.status, b.status),
      dataIndex: "status",
      key: "status",
      fixed: "right",
      width: 100,
      render: (value: string, row: RosterTableRow) => {
        return fixtureId ? (
          <div>{value}</div>
        ) : (
          <RosterStatusEditableColumn
            disabled={isUpsertingMap[row.personId] || false}
            isEditMode={massEditMode}
            defaultValue={value}
            onSave={handleSave(row, "status")}
          />
        );
      },
    },
    {
      title: formatMessage({ id: "roster.caps" }),
      dataIndex: "caps",
      key: "caps",
      fixed: "right",
      width: 50,
    },
    {
      render: (_: unknown, row: RosterTableRow) => (
        <Space style={{ columnGap: "5px" }}>
          <StyledButton size="small" type="text">
            <Link
              to={`/${customerId}/admin/${sport}/${organizationId}/persons/manage/${row.personId}`}
              title={formatMessage({ id: "person.details" })}
            >
              <FormattedMessage id="details" />
            </Link>
          </StyledButton>
          {isRosterEditable && (
            <PermissionCheck
              permissionKeys={fixtureId ? [PermissionNames.FIXTURE_ROSTER] : [PermissionNames.SEASON_ROSTER]}
              permissionOptions={[PermissionOperations.DELETE]}
            >
              <DeleteRosterAction
                onDelete={async (id: string) => {
                  if (row.isCaptain && row.captainRoleId) {
                    await deleteCaptainRole(row.captainRoleId);
                  }
                  await handleDelete(id);
                }}
                record={row}
              />
            </PermissionCheck>
          )}
        </Space>
      ),
      fixed: "right",
      width: 50,
    },
  ];

  return columns;
};
